// Player marketplace — browse + buy listings (paid from your bank), or list your own bag items for a
// price (a 2% upfront fee, from bank). Two tabs: BROWSE (everyone's listings) and SELL (list an item
// + manage your active listings). Mirrors the bank pixel style.

import {
  marketListingFee,
  MARKET_MIN_PRICE,
  ITEM_NAMES,
  gearStat,
  type MarketListingDTO,
} from "@ghostland/shared";
import { marketStore } from "../items/marketStore.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { statsStore } from "../items/statsStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { injectBillStyle, bankIcon } from "./pixelBills.js";

export interface MarketActions {
  request: () => void;
  list: (slotIndex: number, quantity: number, price: number) => void;
  buy: (listingId: string) => void;
  cancel: (listingId: string) => void;
}
let actions: MarketActions | null = null;
export function setMarketActions(a: MarketActions): void {
  actions = a;
}

const STYLE_ID = "ghostland-market-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-mkt-overlay { position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center; background: rgba(0,0,0,.7); color: #e8e8e8; }
#gl-mkt-overlay, #gl-mkt-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-mkt-overlay.open { display: flex; }
#gl-mkt-card { width: 560px; max-height: 86vh; display: flex; flex-direction: column; padding: 20px 18px 16px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-mkt-head { display: flex; align-items: baseline; justify-content: space-between; }
#gl-mkt-head h1 { margin: 0; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-mkt-bank { font-size: 9px; color: #d8d8d8; display: inline-flex; align-items: center; gap: 5px; }
#gl-mkt-tabs { display: flex; gap: 8px; margin: 12px 0 10px; }
.gl-mkt-tab { flex: 1; padding: 6px 0; text-align: center; background: #000; border: 2px solid #2f2f2f; color: #8a8a8a; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
.gl-mkt-tab.on { border-color: #f4f4f4; color: #f4f4f4; }
#gl-mkt-body { flex: 1; overflow-y: auto; min-height: 220px; }
.gl-mkt-row { display: flex; align-items: center; gap: 10px; padding: 7px 6px; border-bottom: 1px solid #161616; }
.gl-mkt-row canvas { width: 30px; height: 30px; image-rendering: pixelated; flex: 0 0 30px; }
.gl-mkt-row .nm { flex: 1; font-size: 8px; color: #e0e0e0; line-height: 1.5; }
.gl-mkt-row .nm small { color: #7a7a7a; }
.gl-mkt-row .nm .stats .base { color: #cfcfcf; }
.gl-mkt-row .nm .stats .roll { color: #74d86a; }
.gl-mkt-row .pr { font-size: 9px; color: #ffd23f; min-width: 70px; text-align: right; }
.gl-mkt-empty { padding: 26px 8px; text-align: center; font-size: 8px; color: #6a6a6a; }
.gl-mkt-btn { background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; padding: 6px 10px; cursor: pointer; font-size: 7px; letter-spacing: 1px; }
.gl-mkt-btn:hover:not(:disabled) { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
.gl-mkt-btn.buy:hover:not(:disabled) { background: #7bd66a; border-color: #7bd66a; }
.gl-mkt-btn.danger:hover:not(:disabled) { background: #e0584f; border-color: #e0584f; color: #000; }
.gl-mkt-btn:disabled { opacity: .35; cursor: default; }
.gl-mkt-grid { display: grid; gap: 4px; grid-template-columns: repeat(5, 1fr); margin-bottom: 12px; }
.gl-mkt-cell { width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a; position: relative; cursor: pointer; display: flex; align-items: flex-end; justify-content: flex-end; }
.gl-mkt-cell.empty { cursor: default; }
.gl-mkt-cell.sel { border-color: #ffd23f; }
.gl-mkt-cell:not(.empty):hover { border-color: #f4f4f4; }
.gl-mkt-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-mkt-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
#gl-mkt-form { border: 1px solid #2a2a2a; padding: 10px; margin-bottom: 12px; display: none; }
#gl-mkt-form.on { display: block; }
#gl-mkt-form .lbl { font-size: 8px; color: #b0b0b0; }
#gl-mkt-form input { width: 90px; margin: 0 8px; padding: 5px 6px; background: #0c0c0c; border: 1px solid #2a2a2a; color: #f4f4f4; font-size: 9px; }
#gl-mkt-form .fee { font-size: 7px; color: #7a7a7a; margin: 8px 0; }
#gl-mkt-sub { font-size: 8px; color: #8a8a8a; margin: 4px 0 8px; }
#gl-mkt-close { margin-top: 10px; padding: 7px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
#gl-mkt-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
`;

let overlay: HTMLDivElement | null = null;
let bankEl: HTMLDivElement | null = null;
let bodyEl: HTMLDivElement | null = null;
let tabBrowse: HTMLDivElement | null = null;
let tabSell: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];
let tab: "browse" | "sell" = "browse";
let selSlot: number | null = null; // bag slot selected to list (sell tab)

function itemName(id: string): string {
  return ITEM_NAMES[id as keyof typeof ITEM_NAMES] ?? id;
}

function renderBrowse(): void {
  if (!bodyEl) return;
  const { listings } = marketStore.get();
  const bank = statsStore.get().bankMoney;
  const note = document.createElement("div");
  note.id = "gl-mkt-sub";
  note.textContent = "Purchases are paid from your BANK balance";
  if (listings.length === 0) {
    bodyEl.replaceChildren(
      note,
      Object.assign(document.createElement("div"), {
        className: "gl-mkt-empty",
        textContent: "No listings yet. Be the first to sell!",
      }),
    );
    return;
  }
  bodyEl.replaceChildren(note, ...listings.map((l) => row(l, bank)));
}

function row(l: MarketListingDTO, bank: number): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "gl-mkt-row";
  el.appendChild(itemThumb(l.itemId));
  const nm = document.createElement("div");
  nm.className = "nm";
  // Gear stat block (mirrors the inventory tooltip): base "+N Armor +1 <Stat>" then the rolled
  // armored bonus in green. Base stats derive from the itemId; the rolled bonus rides l.mods.
  const STAT_ABBR: Record<string, string> = { strength: "STR", dex: "DEX", agility: "AGI" };
  const g = gearStat(l.itemId);
  const m = l.mods;
  const rolled = m
    ? (["strength", "dex", "agility"] as const).filter((k) => (m[k] ?? 0) > 0).map((k) => `+${m[k]} ${STAT_ABBR[k]}`).join(" ")
    : "";
  const statsTxt = g
    ? `<span class="base">+${g.armor} Armor +${g.amount} ${STAT_ABBR[g.stat] ?? g.stat}</span>${rolled ? ` <span class="roll">${rolled}</span>` : ""}`
    : "";
  nm.innerHTML = `${itemName(l.itemId)}${l.quantity > 1 ? ` ×${l.quantity}` : ""}${statsTxt ? `<br><span class="stats">${statsTxt}</span>` : ""}<br><small>${l.mine ? "your listing" : `by ${l.sellerName}`}</small>`;
  el.appendChild(nm);
  const pr = document.createElement("div");
  pr.className = "pr";
  pr.textContent = `$${l.price.toLocaleString()}`;
  el.appendChild(pr);
  if (l.mine) {
    const b = document.createElement("button");
    b.className = "gl-mkt-btn danger";
    b.textContent = "CANCEL";
    b.addEventListener("click", () => actions?.cancel(l.id));
    el.appendChild(b);
  } else {
    const b = document.createElement("button");
    b.className = "gl-mkt-btn buy";
    b.textContent = "BUY";
    b.disabled = bank < l.price;
    if (b.disabled) b.title = "Not enough bank money";
    b.addEventListener("click", () => actions?.buy(l.id));
    el.appendChild(b);
  }
  return el;
}

function renderSell(): void {
  if (!bodyEl) return;
  const bag = inventoryStore.get();
  const by = new Map(bag.map((s) => [s.slotIndex, s]));
  if (selSlot != null && !by.has(selSlot)) selSlot = null; // item left the bag (e.g. just listed)
  const { listings, myCount, maxListings } = marketStore.get();
  const mine = listings.filter((l) => l.mine);

  bodyEl.innerHTML =
    `<div id="gl-mkt-sub">Listings: ${myCount}/${maxListings} · pick a bag item to list it (2% fee)</div>` +
    `<div class="gl-mkt-grid" id="gl-mkt-bag"></div>` +
    `<div id="gl-mkt-form"></div>` +
    `<div id="gl-mkt-sub">YOUR LISTINGS</div><div id="gl-mkt-mine"></div>`;

  // bag grid — click to select for listing
  const grid = bodyEl.querySelector<HTMLDivElement>("#gl-mkt-bag")!;
  const cells: HTMLDivElement[] = [];
  for (let i = 0; i < 20; i++) {
    const s = by.get(i);
    const el = document.createElement("div");
    el.className =
      "gl-mkt-cell" + (s ? "" : " empty") + (i === selSlot ? " sel" : "");
    if (s) {
      el.appendChild(itemThumb(s.itemId));
      if (s.quantity > 1) {
        const q = document.createElement("span");
        q.className = "q";
        q.textContent = String(s.quantity);
        el.appendChild(q);
      }
      el.addEventListener("click", () => {
        selSlot = i;
        renderSell();
      });
    }
    cells.push(el);
  }
  grid.replaceChildren(...cells);

  // list form for the selected slot
  const form = bodyEl.querySelector<HTMLDivElement>("#gl-mkt-form")!;
  const sel = selSlot != null ? by.get(selSlot) : undefined;
  if (sel) {
    form.classList.add("on");
    form.innerHTML =
      `<div class="lbl">List <b>${itemName(sel.itemId)}</b></div>` +
      `<div style="margin-top:8px"><span class="lbl">Qty</span><input id="gl-mkt-qty" type="number" min="1" max="${sel.quantity}" value="${sel.quantity}">` +
      `<span class="lbl">Price $</span><input id="gl-mkt-price" type="number" min="${MARKET_MIN_PRICE}" value="100"></div>` +
      `<div class="fee" id="gl-mkt-fee"></div>` +
      `<button class="gl-mkt-btn" id="gl-mkt-listbtn">LIST IT</button>`;
    const qtyEl = form.querySelector<HTMLInputElement>("#gl-mkt-qty")!;
    const priceEl = form.querySelector<HTMLInputElement>("#gl-mkt-price")!;
    const feeEl = form.querySelector<HTMLDivElement>("#gl-mkt-fee")!;
    const showFee = (): void => {
      const p = Math.max(0, Math.floor(Number(priceEl.value) || 0));
      feeEl.textContent = `Listing fee: $${marketListingFee(p).toLocaleString()} (from bank)`;
    };
    showFee();
    priceEl.addEventListener("input", showFee);
    form.querySelector("#gl-mkt-listbtn")!.addEventListener("click", () => {
      const qty = Math.max(
        1,
        Math.min(sel.quantity, Math.floor(Number(qtyEl.value) || 1)),
      );
      const price = Math.floor(Number(priceEl.value) || 0);
      if (price < MARKET_MIN_PRICE) return;
      actions?.list(selSlot!, qty, price);
      selSlot = null;
    });
  } else form.classList.remove("on");

  // your active listings (cancel)
  const mineEl = bodyEl.querySelector<HTMLDivElement>("#gl-mkt-mine")!;
  if (mine.length === 0)
    mineEl.innerHTML = `<div class="gl-mkt-empty">You have no active listings.</div>`;
  else
    mineEl.replaceChildren(
      ...mine.map((l) => row(l, statsStore.get().bankMoney)),
    );
}

function refresh(): void {
  if (bankEl)
    bankEl.innerHTML = `${bankIcon()} <b>${statsStore.get().bankMoney.toLocaleString()}</b>`;
  tabBrowse?.classList.toggle("on", tab === "browse");
  tabSell?.classList.toggle("on", tab === "sell");
  if (tab === "browse") renderBrowse();
  else renderSell();
}

function ensure(): void {
  injectBillStyle();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-mkt-overlay";
  overlay.innerHTML = `
    <div id="gl-mkt-card">
      <div id="gl-mkt-head"><h1>MARKETPLACE</h1><div id="gl-mkt-bank">${bankIcon()} <b>0</b></div></div>
      <div id="gl-mkt-tabs"><div class="gl-mkt-tab on" id="gl-mkt-tb">BROWSE</div><div class="gl-mkt-tab" id="gl-mkt-ts">SELL</div></div>
      <div id="gl-mkt-body"></div>
      <button id="gl-mkt-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  bankEl = overlay.querySelector("#gl-mkt-bank");
  bodyEl = overlay.querySelector("#gl-mkt-body");
  tabBrowse = overlay.querySelector("#gl-mkt-tb");
  tabSell = overlay.querySelector("#gl-mkt-ts");
  tabBrowse!.addEventListener("click", () => {
    tab = "browse";
    refresh();
  });
  tabSell!.addEventListener("click", () => {
    tab = "sell";
    refresh();
  });
  overlay
    .querySelector("#gl-mkt-close")!
    .addEventListener("click", closeMarket);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeMarket();
  });
}

export function openMarket(): void {
  ensure();
  tab = "browse";
  selSlot = null;
  overlay!.classList.add("open");
  unsub = [
    marketStore.subscribe(refresh),
    inventoryStore.subscribe(refresh),
    statsStore.subscribe(refresh),
  ];
  actions?.request();
}

export function closeMarket(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn());
  unsub = [];
}

export function isMarketOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
